<template>
  <v-container class="mt-2">
    <v-row v-if="mounted && photographers.length" class="mb-12">
      <v-col cols="12">
        <v-row class="align-end" no-gutters>
          <v-col cols="12" sm="8" lg="10">
            <h1 class="text-h4 font-weight-bold mb-2">Photographers</h1>
            <h2 class="text-subtitle-1">
              It is the photographer, not the camera, that is the instrument.
            </h2>
          </v-col>
          <v-col class="text-right" cols="12" sm="4" lg="2">
            <Filters
              :categories="categories"
              :countries="countries"
              :on-apply="onApply"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        v-for="(photographer, index) in photographers"
        :key="index"
        cols="12"
        sm="6"
        md="4"
        lg="3"
        xl="2"
      >
        <v-card outlined class="mb-1">
          <nuxt-link
            :to="`/photographer/${photographer.userName || photographer.id}`"
            nuxt
          >
            <v-img
              v-if="photographer.bestUserPhoto"
              :src="$asa.getImageUrl(photographer.bestUserPhoto, 'thumb')"
              :lazy-src="$asa.getImageUrl(photographer.bestUserPhoto, 'thumb', true)"
              :height="130"
            ></v-img>
            <div v-else class="tiles"></div>
          </nuxt-link>
          <v-card-title primary-title>
            <div class="text-center w-100-percentage hero-overlap">
              <nuxt-link
                :to="`/photographer/${
                  photographer.userName || photographer.id
                }`"
                nuxt
              >
                <v-avatar size="60px">
                  <img
                    :src="photographer.avatarUrl"
                    :alt="`${photographer.fullName} Avatar`"
                    :class="
                      $vuetify.theme.dark ? 'black-border' : 'white-border'
                    "
                  />
                </v-avatar>
              </nuxt-link>
              <nuxt-link
                :to="`/photographer/${
                  photographer.userName || photographer.id
                }`"
                class="no-underline"
                nuxt
              >
                <h3 class="text-h6 font-weight-bold text-truncate mt-4">
                  {{ photographer.fullName }}
                </h3>
              </nuxt-link>
              <h4 class="text-subtitle-2 text-uppercase font-weight-light">
                <span v-if="photographer.country" class="mr-1 va-top">
                  <img
                    :src="`https://www.flagcdn.com/h20/${photographer.country.toLowerCase()}.png`"
                    :srcset="`https://flagcdn.com/h20/${photographer.country.toLowerCase()}.png 2x`"
                  />
                </span>
                {{ photographer.role
                }}{{ photographer.isCritic ? ' &amp; CRITIC' : '' }}
              </h4>
              <h5 class="text-subtitle-2 text-uppercase font-weight-light">
                <span>
                  <v-icon small class="mx-1">fal fa-trophy</v-icon>
                  {{ photographer.reputation ? photographer.reputation.toLocaleString() : 0 }}
                </span>
                <span
                  v-if="photographer.hasAnActiveSubscription && photographer.subscriptionType !== types.subscription.type.basic"
                  :class="
                    $vuetify.theme.dark
                      ? 'primary black--text'
                      : 'primary secondary--text'
                  "
                  class="pro-badge text-caption font-weight-bold ml-1"
                >
                  <small>
                    {{
                      photographer &&
                      photographer.subscriptionType ===
                        types.subscription.type.pro
                        ? 'PRO'
                        : 'PRO +'
                    }}
                  </small>
                </span>
              </h5>
              <div class="mt-1 mb-n3">
                <ul class="list-inline">
                  <li
                    v-if="
                      !photographer.website &&
                      !photographer.facebook &&
                      !photographer.twitter &&
                      !photographer.instagram &&
                      !photographer.isPortfolioActive
                    "
                  >
                    <v-btn icon disabled><v-icon small></v-icon></v-btn>
                  </li>
                  <li v-if="photographer.website">
                    <v-btn
                      :href="`https://${photographer.website}`"
                      target="_blank"
                      icon
                      ><v-icon small>fal fa-globe</v-icon></v-btn
                    >
                  </li>
                  <li v-if="photographer.facebook">
                    <v-btn
                      :href="`https://www.facebook.com/${photographer.facebook}`"
                      icon
                      target="_blank"
                      ><v-icon small>fab fa-facebook-f</v-icon></v-btn
                    >
                  </li>
                  <li v-if="photographer.twitter">
                    <v-btn
                      :href="`https://twitter.com/${photographer.twitter}`"
                      icon
                      target="_blank"
                      ><v-icon small>fab fa-twitter</v-icon></v-btn
                    >
                  </li>
                  <li v-if="photographer.instagram">
                    <v-btn
                      :href="`https://www.instagram.com/${photographer.instagram}`"
                      icon
                      target="_blank"
                      ><v-icon small>fab fa-instagram</v-icon></v-btn
                    >
                  </li>
                  <li
                    v-if="
                      photographer.portfolioUrl &&
                      photographer.isPortfolioActive
                    "
                  >
                    <v-btn
                      :href="`${portfolioDomain}/${photographer.portfolioUrl}`"
                      icon
                      target="_blank"
                      ><v-icon small>fal fa-briefcase</v-icon></v-btn
                    >
                  </li>
                </ul>
              </div>
            </div>
          </v-card-title>
          <v-card-actions>
            <div class="text-center w-100-percentage">
              <v-btn
                :color="$vuetify.theme.dark ? 'secondary' : 'primary'"
                depressed
                small
                class="mx-1 mb-5"
                @click="showQuickProfile(photographer)"
                >view</v-btn
              >
              <v-btn
                v-if="!me || (me && me.id !== photographer.id)"
                :dark="!photographer.isFollowing"
                :color="photographer.isFollowing ? 'tertiary' : 'bluezone'"
                :loading="loadingFollow"
                depressed
                small
                class="mx-1 mb-5"
                @click="followUser(photographer.id, photographer.isFollowing)"
                >{{ photographer.isFollowing ? 'unfollow' : 'follow' }}</v-btn
              >
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <div v-intersect="onIntersect" />
    <div v-if="loading" class="text-center">
      <v-icon class="fa-spin my-3"> fal fa-spinner </v-icon>
    </div>
    <div
      v-if="!loading && photographers.length === 0 && mounted"
      class="text-center w-100-percentage mt-12"
    >
      <div>
        <v-icon class="d-block mb-3">far fa-sad-cry</v-icon>
        No photographers found for this search
      </div>
    </div>
    <v-navigation-drawer
      v-model="showQuickProfileDrawer"
      fixed
      right
      temporary
      width="460"
      @input="onShortProfile"
    >
      <div v-if="$vuetify.breakpoint.smAndDown" class="text-right">
        <v-btn icon class="btn-close" @click="hideQuickProfile">
          <v-icon>fal fa-times</v-icon>
        </v-btn>
      </div>
      <ShortProfile
        :photographer="selectedPhotographer"
        :load-data="loadShortProfileData"
      />
    </v-navigation-drawer>
  </v-container>
</template>

<script>

import { mapState } from 'vuex'
import Filters from '@/components/photographers/Filters.vue'
import types from '@/utils/types'
import { EventBus } from '@/plugins/event-bus'

export default {
  components: {
    Filters,
    ShortProfile: () => import('@/components/photographers/ShortProfile.vue')
  },
  data() {
    return {
      loading: false,
      loadingFollow: false,
      searchPhotographerValue: '',
      types,
      showQuickProfileDrawer: false,
      selectedPhotographer: undefined,
      loadShortProfileData: false,
      mounted: false,
      portfolioDomain: process.env.portfolioDomain
    }
  },
  async fetch({ store, $asa }) {
    try {
      await store.dispatch('account/getCountries')
      await store.dispatch('account/getCategories')
    } catch (error) {
      $asa.appInsights('Pages: photographers.vue - method: fetch', error)
    }
  },
  head({ $seo }) {
    return $seo({
      title: 'Photographers',
      description:
        "Check 100ASA's list of amateurs and professional photographers."
    })
  },
  computed: {
    ...mapState({
      me: state => state.auth.user,
      categories: state => state.account.categories,
      countries: state => state.account.countries,
      photographers: state => state.users.photographers,
      photographersIsLastPage: state => state.users.photographersIsLastPage,
      photographersPage: state => state.users.photographersPage
    }),
    autoLoadDisabled() {
      return this.loading || this.photographers.length === 0
    }
  },
  async mounted() {
    this.mounted = true
    if (
      !this.photographersIsLastPage &&
      !this.loading &&
      this.photographers.length === 0
    ) {
      try {
        this.loading = true
        await this.$store.dispatch('users/getUsers', {
          page: 1,
          useFilters: true
        })
      } catch (error) {
        this.$asa.appInsights('Pages: photographers.vue - method: mounted', error)
      } finally {
        this.loading = false
      }
    }
  },
  methods: {
    onIntersect(entries) {
      if (
        entries[0].intersectionRatio > 0 &&
        this.photographers.length > 0 &&
        !this.photographersIsLastPage &&
        !this.loading
      ) {
        this.loadMore()
      }
    },
    onShortProfile(value) {
      this.loadShortProfileData = value
    },
    showQuickProfile(photographer) {
      this.selectedPhotographer = photographer
      this.showQuickProfileDrawer = true
    },
    hideQuickProfile() {
      this.showQuickProfileDrawer = false
    },
    async loadMore() {
      try {
        this.loading = true
        const page = this.photographersPage + 1
        await this.$store.dispatch('users/getUsers', {
          page,
          useFilters: true
        })
      } catch (error) {
        this.$asa.appInsights('Pages: photographers.vue - method: loadMore', error)
      } finally {
        this.loading = false
      }
    },
    async followUser(id, following) {
      if (this.me) {
        try {
          this.loadingFollow = true
          await this.$store.dispatch(
            `following/${following ? 'unfollow' : 'follow'}`,
            {
              userId: id
            }
          )
          await this.$store.dispatch('feeds/resetStore')
          await this.$auth.fetchUser()
        } catch (error) {
          this.$asa.appInsights('Pages: photographers.vue - method: followUser', error)
        } finally {
          this.loadingFollow = false
        }
      } else {
        EventBus.$emit('snackbar', {
          text: 'You need to log in to follow other photographers',
          color: 'info'
        })
      }
    },
    async onApply(filters) {
      await this.$store.dispatch('users/resetPhotographers')
      await this.$store.dispatch('users/setFilters', filters)
      try {
        this.loading = true
        await this.$store.dispatch('users/getUsers', {
          page: 1,
          useFilters: true
        })
      } catch (error) {
        this.$asa.appInsights('Pages: photographers.vue - method: onApply', error)
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>

.tiles {
  background: url('/images/photographers/triangles.svg') 0 0 no-repeat
    transparent;
  background-size: cover;
  height: 130px;
}
.hero-overlap {
  margin-top: -45px;
}
.white-border {
  border: 2px solid #fafafa;
}
.black-border {
  border: 2px solid #212121;
}
.pro-badge {
  border-radius: 4px;
  padding: 0 5px;
}
.btn-close {
  margin: 14px 14px 0 0;
}
.h-36 {
  height: 24px;
}
</style>
